/**
 * The files a message carried, drawn as cards by what they are.
 *
 * One family, two homes: under a bubble on 对话, and inside a 今天 card that
 * says what the Agent did. The Owner's reference draws every produced artifact
 * as a card of its own, and the point of doing it by media type is that the
 * shell never learns which plugin made the file — a film from the producer and
 * a film somebody forwarded are one card (`kind.ts`).
 *
 * What is quoted and what is the shell's own words is the other rule here. The
 * file's *name* is whoever made it talking — `ep1.mp4`, `第一集-稿本.pdf` — so it
 * sits inside `[data-quoted]`, where the Owner vocabulary gate reads it as
 * somebody's words rather than as this shell writing a filename onto an Owner
 * page. Everything outside that: 「视频」「12 MB」「下载」, in a person's words,
 * checked by the gate like every other sentence the shell writes.
 */

import { useRef, useState, type CSSProperties } from "react";
import { Icon, type IconName } from "../Icon";
import {
  attachmentUrl,
  fileSizeWords,
  mediaKind,
  posterUrl,
  typeWords,
  type Attachment,
  type MediaKind,
} from "./kind";
import { useServedSize } from "./servedSize";
import "./attachments.css";

/** Where a film with no cover of its own is first drawn; the 内容目录 thumbnails look at the same moment. */
const FIRST_FRAME = "#t=0.5";

const GLYPH: Record<MediaKind, IconName> = {
  picture: "picture",
  movie: "clapperboard",
  sound: "sound",
  document: "file",
  file: "file",
};

export function AttachmentCards({ items }: { items: Attachment[] | undefined }) {
  if (!items?.length) return null;
  return (
    <ul className="attach" data-testid="attachments">
      {items.map((item, index) => (
        // A message may carry the same file twice — a cover frame that is also
        // an attachment of its own — so the index is part of the key.
        <li key={`${item.uri}#${index}`} className="attach-item" data-kind={mediaKind(item)}>
          <AttachmentCard item={item} />
        </li>
      ))}
    </ul>
  );
}

function AttachmentCard({ item }: { item: Attachment }) {
  const kind = mediaKind(item);
  const url = attachmentUrl(item.uri);
  if (kind === "picture") {
    return (
      <>
        <div className="attach-media">
          <figure className="attach-figure" data-quoted="true">
            <a href={url} target="_blank" rel="noreferrer noopener">
              <img src={url} alt={item.name || "附件图片"} loading="lazy" />
            </a>
          </figure>
          <Download item={item} url={url} />
        </div>
        <Foot item={item} />
      </>
    );
  }
  if (kind === "movie") {
    return (
      <>
        <div className="attach-media">
          <FilmPlayer item={item} url={url} />
          <Download item={item} url={url} />
        </div>
        <Foot item={item} />
      </>
    );
  }
  if (kind === "sound") {
    return (
      <>
        <InlinePlayer item={item} url={url} kind="sound" />
        <Foot item={item} />
      </>
    );
  }
  if (kind === "document") {
    // A document's browser viewer can appear as a large blank or black frame
    // inside a long conversation. Keep the chat compact and open it separately.
    return <FileCard item={item} url={url} open />;
  }
  return <FileCard item={item} url={url} />;
}

/**
 * The player. Until the film says how big it is, the box is the vertical shape most
 * films here have (`attach-player.is-pending`); a film that cannot be read stops
 * holding the space for one.
 */
function FilmPlayer({ item, url }: { item: Attachment; url: string }) {
  return <InlinePlayer item={item} url={url} kind="movie" />;
}

function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** Flat controls live below the media, so the browser's floating pill never covers the frame. */
function InlinePlayer({ item, url, kind }: { item: Attachment; url: string; kind: "movie" | "sound" }) {
  const player = useRef<HTMLMediaElement | null>(null);
  const frame = useRef<HTMLDivElement | null>(null);
  const [sized, setSized] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const poster = kind === "movie" ? posterUrl(item) : undefined;
  const mediaEvents = {
    onLoadedMetadata: () => {
      setSized(true); setFailed(false); setRetrying(false);
      const length = player.current?.duration ?? 0;
      setDuration(Number.isFinite(length) ? length : 0);
    },
    onError: () => { setSized(true); setFailed(true); setRetrying(false); setPlaying(false); },
    onTimeUpdate: () => setCurrent(player.current?.currentTime ?? 0),
    onPlay: () => setPlaying(true),
    onPause: () => setPlaying(false),
    onEnded: () => setPlaying(false),
    onVolumeChange: () => setMuted(player.current?.muted ?? false),
  };
  const toggle = () => {
    const media = player.current;
    if (!media) return;
    if (media.paused) void media.play().catch(() => setPlaying(false));
    else media.pause();
  };
  return (
    <div ref={frame} className="attach-media-player">
      {kind === "movie" ? <video
        ref={node => { player.current = node; }}
        className={sized ? "attach-player" : "attach-player is-pending"}
        data-testid="attach-player"
        playsInline
        preload="metadata"
        poster={poster}
        aria-label="视频"
        src={poster ? url : `${url}${FIRST_FRAME}`}
        {...mediaEvents}
      /> : <audio ref={node => { player.current = node; }} preload="metadata" src={url} {...mediaEvents} />}
      {!failed ? <div className="attach-media-controls" role="group" aria-label={kind === "movie" ? "视频播放控制" : "音频播放控制"}>
        <button type="button" aria-label={playing ? "暂停" : "播放"} onClick={toggle}>
          <Icon name={playing ? "pause" : "play"} size={17} />
        </button>
        <input type="range" min="0" max={duration || 1} step="0.1" value={Math.min(current, duration || 1)}
          disabled={!duration} aria-label="播放进度" style={{ "--media-played": `${duration ? current / duration * 100 : 0}%` } as CSSProperties}
          onChange={event => { if (player.current) player.current.currentTime = Number(event.currentTarget.value); }} />
        <time>{clock(current)} / {clock(duration)}</time>
        <button type="button" aria-label={muted ? "取消静音" : "静音"} onClick={() => {
          if (player.current) player.current.muted = !player.current.muted;
        }}><Icon name="sound" size={16} /></button>
        {kind === "sound" ? <Download item={item} url={url} /> : null}
        {kind === "movie" ? <button type="button" aria-label="全屏" onClick={() => {
          void frame.current?.requestFullscreen?.();
        }}><Icon name="expand" size={16} /></button> : null}
      </div> : null}
      {failed ? <div className="attach-read-notice" role="alert">
        <p><span data-quoted="true">{item.name || (kind === "movie" ? "这份视频" : "这份音频")}</span>读取失败。</p>
        <button type="button" className="btn btn-quiet" onClick={() => {
          setFailed(false); setSized(false); setRetrying(true);
          // Reopen the original authorized URL; never retain a signed redirect.
          player.current?.load();
        }}>{kind === "movie" ? "重新读取此视频" : "重新读取此音频"}</button>
      </div> : retrying ? <p className="attach-read-notice micro" role="status">正在重新读取这份{kind === "movie" ? "视频" : "音频"}…</p> : null}
    </div>
  );
}

/** The name, the kind and the size, under whatever was drawn above them. */
function Foot({ item }: { item: Attachment }) {
  return (
    <div className="attach-foot">
      <div className="attach-foot-copy">
        <p className="attach-name" data-quoted="true">
          {item.name || "一个文件"}
        </p>
        <Meta item={item} />
      </div>
    </div>
  );
}

/**
 * A file with nothing to show: what it is, how big, and one way to open it.
 *
 * `open` is the pdf fallback — a document the browser will not draw is still a
 * document a person can open in a tab of its own, and 「下载」 would be the wrong
 * verb for it.
 */
function FileCard({ item, url, open = false }: { item: Attachment; url: string; open?: boolean }) {
  return (
    <div className="attach-file">
      <Icon name={GLYPH[mediaKind(item)]} size={23} className="attach-glyph" />
      <div className="attach-foot-copy">
        <p className="attach-name" data-quoted="true">
          {item.name || "一个文件"}
        </p>
        <Meta item={item} />
      </div>
      {open ? (
        <a className="btn btn-quiet" href={url} target="_blank" rel="noreferrer noopener">
          打开
        </a>
      ) : (
        <Download item={item} url={url} />
      )}
    </div>
  );
}

function Download({ item, url }: { item: Attachment; url: string }) {
  return (
    <a className="btn btn-quiet attach-get" href={url} download={item.name || undefined}
      aria-label={`下载${item.name || "附件"}`} title="下载">
      <Icon name="download-simple" size={17} />
    </a>
  );
}

/**
 * 「视频 · 12 MB」, or just 「视频」 while it is not known how big the file is.
 *
 * The size is what the bytes route answers about the file as it is now, never
 * a number the message carried (`servedSize.ts`).
 */
function Meta({ item }: { item: Attachment }) {
  const size = fileSizeWords(useServedSize(attachmentUrl(item.uri)));
  return <p className="micro attach-meta">{size ? `${typeWords(item)} · ${size}` : typeWords(item)}</p>;
}
